import type { TriggerState } from "../../lib/yaml-serializer.js";
import { ThemeToggle } from "../ThemeToggle.js";

interface CanvasToolbarProps {
	workflowName: string;
	version: number;
	trigger: TriggerState;
	onNameChange: (name: string) => void;
	onTriggerChange: (trigger: TriggerState) => void;
	onYamlToggle: () => void;
	onDeploy: () => void;
	yamlOpen: boolean;
	deploying: boolean;
	onRun?: () => void;
}

export function CanvasToolbar({
	workflowName,
	version,
	trigger,
	onNameChange,
	onTriggerChange,
	onYamlToggle,
	onDeploy,
	yamlOpen,
	deploying,
	onRun,
}: CanvasToolbarProps) {
	return (
		<div className="absolute left-3 right-3 top-3 z-10 flex items-center justify-between">
			<div className="flex items-center gap-2">
				<input
					type="text"
					value={workflowName}
					onChange={(e) => onNameChange(e.target.value)}
					className="rounded-xl border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text-primary focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
				/>
				<select
					value={trigger.type}
					onChange={(e) => {
						const type = e.target.value as "api" | "manual" | "webhook";
						if (type === "webhook") onTriggerChange({ type: "webhook", path: "/incoming/" });
						else onTriggerChange({ type });
					}}
					className="rounded-xl border border-border bg-surface px-2 py-1.5 text-[11px] text-text-muted focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
				>
					<option value="api">API</option>
					<option value="manual">Manual</option>
					<option value="webhook">Webhook</option>
				</select>
				{trigger.type === "webhook" && (
					<input
						type="text"
						value={trigger.path}
						onChange={(e) => onTriggerChange({ type: "webhook", path: e.target.value })}
						placeholder="/incoming/path"
						className="rounded-xl border border-border bg-surface px-2 py-1.5 text-[11px] text-text-primary focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
					/>
				)}
				<span className="rounded-xl border border-border bg-surface px-2 py-1.5 text-[11px] text-text-muted">
					v{version}
				</span>
			</div>
			<div className="flex items-center gap-2">
				<button
					type="button"
					onClick={onYamlToggle}
					className={`rounded-xl border px-3 py-1.5 text-xs transition-colors ${
						yamlOpen
							? "border-accent bg-accent-bg text-accent"
							: "border-border bg-surface text-text-muted hover:text-text-primary"
					}`}
				>
					{"{ } YAML"}
				</button>
				<button
					type="button"
					onClick={onDeploy}
					disabled={deploying}
					className="rounded-xl bg-accent px-4 py-1.5 text-xs font-semibold text-accent-text shadow-sm transition-opacity duration-100 hover:opacity-90 disabled:opacity-50"
				>
					{deploying ? "Deploying..." : "Deploy"}
				</button>
				{onRun && (
					<button
						type="button"
						onClick={onRun}
						className="rounded-xl border border-success/50 bg-success/10 px-4 py-1.5 text-xs font-semibold text-success transition-opacity duration-100 hover:opacity-90"
					>
						Run
					</button>
				)}
				<ThemeToggle />
			</div>
		</div>
	);
}
